import React from 'react';
import { useAccessibility } from '../../hooks/useAccessibility';
import HighContrastToggle from './HighContrastToggle';
import { SkipToMainLink } from './ScreenReaderHelpers';
import AccessibilityChecker from '../../tools/AccessibilityChecker';

interface AccessibilityPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AccessibilityPanel: React.FC<AccessibilityPanelProps> = ({ isOpen, onClose }) => {
  const {
    settings,
    updateSetting,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
  } = useAccessibility();

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="accessibility-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full m-4">
        <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 id="accessibility-title" className="text-2xl font-bold text-gray-900 dark:text-white">
            Accessibility Settings
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded p-1"
            aria-label="Close accessibility settings"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Skip to main content helper */}
          <SkipToMainLink />

          {/* High Contrast */}
          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="high-contrast" className="text-sm font-medium text-gray-900 dark:text-white">
                High Contrast Mode
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Increase contrast for better visibility
              </p>
            </div>
            <HighContrastToggle />
          </div>

          {/* Font Size */}
          <div>
            <label className="text-sm font-medium text-gray-900 dark:text-white block mb-2">
              Font Size
            </label>
            <div className="flex items-center gap-4">
              <button
                onClick={decreaseFontSize}
                disabled={settings.fontSize === 'small'}
                className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Decrease font size"
              >
                A-
              </button>
              <span className="flex-1 text-center text-sm text-gray-700 dark:text-gray-300 capitalize">
                {settings.fontSize}
              </span>
              <button
                onClick={increaseFontSize}
                disabled={settings.fontSize === 'x-large'}
                className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Increase font size"
              >
                A+
              </button>
              <button
                onClick={resetFontSize}
                className="px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                aria-label="Reset font size"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Reduced Motion */}
          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="reduced-motion" className="text-sm font-medium text-gray-900 dark:text-white">
                Reduced Motion
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Minimize animations and transitions
              </p>
            </div>
            <button
              id="reduced-motion"
              role="switch"
              aria-checked={settings.reducedMotion}
              onClick={() => updateSetting('reducedMotion', !settings.reducedMotion)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.reducedMotion ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.reducedMotion ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Keyboard Shortcuts */}
          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="keyboard-shortcuts" className="text-sm font-medium text-gray-900 dark:text-white">
                Keyboard Shortcuts
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Enable keyboard navigation shortcuts
              </p>
            </div>
            <button
              id="keyboard-shortcuts"
              role="switch"
              aria-checked={settings.keyboardShortcuts}
              onClick={() => updateSetting('keyboardShortcuts', !settings.keyboardShortcuts)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.keyboardShortcuts ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.keyboardShortcuts ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Focus Indicators */}
          <div>
            <label htmlFor="focus-indicators" className="text-sm font-medium text-gray-900 dark:text-white block mb-2">
              Focus Indicators
            </label>
            <select
              id="focus-indicators"
              value={settings.focusIndicators}
              onChange={(e) => updateSetting('focusIndicators', e.target.value as any)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="default">Default</option>
              <option value="enhanced">Enhanced</option>
              <option value="maximum">Maximum Visibility</option>
            </select>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Control the visibility of focus outlines
            </p>
          </div>

          {/* Screen Reader Mode */}
          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="screen-reader" className="text-sm font-medium text-gray-900 dark:text-white">
                Screen Reader Optimizations
              </label>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Enhance experience for screen reader users
              </p>
            </div>
            <button
              id="screen-reader"
              role="switch"
              aria-checked={settings.screenReaderMode}
              onClick={() => updateSetting('screenReaderMode', !settings.screenReaderMode)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                settings.screenReaderMode ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.screenReaderMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {/* Inline Accessibility Checker */}
          <AccessibilityChecker />
        </div>

        <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Settings are saved automatically
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
